import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
	BadgeCheck,
	CalendarClock,
	Clock3,
	Info,
	Sparkles,
	Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	SHOWCASE_MIN_SESSIONS_TO_DISPLAY,
	getApproach,
	getFocusTopic,
	getHighlightTag,
} from '@/constants/professionalTags';
import { useGetProfessionalShowcase } from '@/services/queries/professional';
import { useGetPublicAvailableDate } from '@/services/queries/available-date';
import { HighlightTagChip } from './ProfessionalTagChips';

const SAO_PAULO_TZ = 'America/Sao_Paulo';
const NEXT_SLOTS_COUNT = 4;
/** Mesma folga mínima usada no passo de horário. */
const MINIMUM_LEAD_TIME_MINUTES = 10;

/** Horário livre do profissional (mesmo formato do endpoint público de disponibilidade). */
export interface ShowcaseSlot {
	start_time: string;
	end_time: string;
	available_date_id: string;
}

export interface ProfessionalShowcasePanelProps {
	/** id, nomes, foto, video_url, crm_or_crp, brief_description, tags... */
	professional: Professionals.PublicCardData;
	serviceName?: string;
	/**
	 * CTA "Agendar com {first_name}" (sem slot) e clique num horário (com slot,
	 * para o próximo passo abrir com ele já selecionado).
	 */
	onSchedule?: (slot?: ShowcaseSlot) => void;
	/** Mantido por compatibilidade; o fechamento fica no X do modal. */
	onClose?: () => void;
	/** true quando o profissional está vendo o próprio perfil: mesmo visual, CTA desabilitado. */
	previewMode?: boolean;
}

const getYouTubeId = (url: string) => {
	if (url.includes('youtu.be/')) {
		return url.split('youtu.be/')[1].split(/[?&#]/)[0] || null;
	}
	const regExp =
		/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|&v=)([^#&?]*).*/;
	const match = url.match(regExp);
	return match && match[2].length === 11 ? match[2] : null;
};

const weekdayFormatter = new Intl.DateTimeFormat('pt-BR', {
	weekday: 'short',
	timeZone: SAO_PAULO_TZ,
});
const dayMonthFormatter = new Intl.DateTimeFormat('pt-BR', {
	day: '2-digit',
	month: '2-digit',
	timeZone: SAO_PAULO_TZ,
});
const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
	hour: '2-digit',
	minute: '2-digit',
	hour12: false,
	timeZone: SAO_PAULO_TZ,
});
const monthYearFormatter = new Intl.DateTimeFormat('pt-BR', {
	month: 'long',
	year: 'numeric',
	timeZone: SAO_PAULO_TZ,
});

/** "seg, 09/09 · 09:00" (America/Sao_Paulo). */
const formatSlotLabel = (iso: string) => {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '';
	const weekday = weekdayFormatter.format(date).replace('.', '');
	return `${weekday}, ${dayMonthFormatter.format(
		date,
	)} · ${timeFormatter.format(date)}`;
};

const formatMemberSince = (iso?: string | null) => {
	if (!iso) return '';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '';
	return monthYearFormatter.format(date);
};

const formatCount = (value: number) =>
	new Intl.NumberFormat('pt-BR').format(value);

/**
 * O ModalDrawer monta o conteúdo duas vezes (dialog desktop + sheet mobile, um
 * deles escondido via CSS). Só a cópia visível deve montar o iframe do vídeo,
 * senão o áudio toca em dobro.
 */
const useIsRendered = () => {
	const ref = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(false);
	useEffect(() => {
		const check = () =>
			setVisible(
				Boolean(ref.current && ref.current.getClientRects().length),
			);
		check();
		window.addEventListener('resize', check);
		return () => window.removeEventListener('resize', check);
	}, []);
	return { ref, visible };
};

const SectionLabel = ({
	icon: Icon,
	children,
}: {
	icon: LucideIcon;
	children: ReactNode;
}) => (
	<div className="flex items-center gap-2 text-zinc-500">
		<Icon className="h-4 w-4" />
		<span className="text-[11px] font-black uppercase tracking-[0.18em]">
			{children}
		</span>
		<div className="ml-1 h-px flex-1 bg-gradient-to-r from-zinc-200 via-zinc-200/60 to-transparent" />
	</div>
);

/**
 * Painel "Conheça o profissional": foto quadrada grande ao lado do nome e das
 * tags, vídeo em autoplay logo abaixo (quando houver), e então bio, abordagem,
 * temas, números e próximos horários. Usado no modal dos cards e no preview do
 * perfil do profissional.
 */
const ProfessionalShowcasePanel = ({
	professional,
	serviceName,
	onSchedule,
	previewMode = false,
}: ProfessionalShowcasePanelProps) => {
	const {
		id,
		first_name,
		last_name,
		name_prefix,
		crm_or_crp,
		profile_photo_url,
		brief_description,
		video_url,
		highlight_tag,
		main_approach,
		secondary_approaches,
		focus_topics,
	} = professional;

	const hasId = Boolean(id);
	const normalizedService = serviceName?.toLowerCase() || '';
	const isDoctor =
		normalizedService.includes('médico') ||
		normalizedService.includes('psiquiatra');
	const documentLabel = isDoctor ? 'CRM' : 'CRP';
	const fullName = [name_prefix, first_name, last_name]
		.filter(Boolean)
		.join(' ');
	const initials = [first_name, last_name]
		.filter(Boolean)
		.map((part) => part.trim().charAt(0).toUpperCase())
		.join('')
		.slice(0, 2);
	const description = brief_description?.trim();

	const highlight = getHighlightTag(highlight_tag);
	const mainApproachMeta = getApproach(main_approach);
	const secondaryApproachMetas = (secondary_approaches ?? [])
		.map((key) => getApproach(key))
		.filter((meta): meta is NonNullable<typeof meta> => Boolean(meta));
	const topics = (focus_topics ?? [])
		.map((key) => getFocusTopic(key))
		.filter((meta): meta is NonNullable<typeof meta> => Boolean(meta));

	const youtubeId = video_url ? getYouTubeId(video_url.trim()) : null;

	const { data: showcase, isLoading: isShowcaseLoading } =
		useGetProfessionalShowcase(id, { enabled: hasId });
	const showNumbers =
		!!showcase &&
		showcase.sessions_count > SHOWCASE_MIN_SESSIONS_TO_DISPLAY;
	const memberSince = formatMemberSince(showcase?.member_since);

	const { data: availability, isLoading: isAvailabilityLoading } =
		useGetPublicAvailableDate(id, { enabled: hasId });

	const nextSlots = useMemo<ShowcaseSlot[]>(() => {
		const days = availability?.professionalAvailability?.availability ?? [];
		const earliest = Date.now() + MINIMUM_LEAD_TIME_MINUTES * 60_000;
		return days
			.flatMap((day) => day.hours ?? [])
			.filter((slot) => new Date(slot.start_time).getTime() > earliest)
			.sort(
				(a, b) =>
					new Date(a.start_time).getTime() -
					new Date(b.start_time).getTime(),
			)
			.slice(0, NEXT_SLOTS_COUNT);
	}, [availability]);

	const { ref: rootRef, visible: isRendered } = useIsRendered();

	const canSchedule = !previewMode && Boolean(onSchedule);
	const handleSchedule = (slot?: ShowcaseSlot) => {
		if (!canSchedule) return;
		onSchedule?.(slot);
	};

	// Autoplay com som: o usuário acabou de clicar em "Conheça o profissional",
	// então o navegador já tem o gesto necessário para tocar áudio.
	const embedSrc = youtubeId
		? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&controls=1&rel=0&playsinline=1&modestbranding=1`
		: '';

	return (
		<div
			ref={rootRef}
			className="w-full space-y-6"
			data-testid="professional-showcase"
		>
			{/* Cabeçalho: foto quadrada grande + identificação */}
			<div className="flex items-start gap-4 sm:gap-6">
				<div
					className="relative size-28 shrink-0 overflow-hidden rounded-[1.5rem] bg-zinc-100 shadow-md ring-4 ring-white sm:size-36 md:size-44"
					data-testid="professional-showcase-photo"
				>
					{profile_photo_url ? (
						<img
							src={profile_photo_url}
							alt={fullName}
							referrerPolicy="no-referrer"
							className="absolute inset-0 h-full w-full object-cover"
						/>
					) : (
						<div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-200 text-4xl font-black text-zinc-400">
							{initials || '??'}
						</div>
					)}
				</div>

				<div className="min-w-0 flex-1 space-y-2">
					<div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
						<h2 className="min-w-0 text-xl font-black italic tracking-tight text-stone-900 sm:text-2xl">
							{fullName}
						</h2>
						{highlight ? (
							<HighlightTagChip tag={highlight.key} size="md" />
						) : null}
					</div>
					{serviceName ? (
						<p className="text-[11px] font-black uppercase tracking-[0.18em] text-zinc-600">
							{serviceName}
						</p>
					) : null}
					<p className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-zinc-500">
						<BadgeCheck className="h-3.5 w-3.5 text-emerald-600" />
						Inscrição {documentLabel} {crm_or_crp}
					</p>
					{highlight ? (
						<p className="text-xs font-medium text-zinc-500">
							{highlight.hint}
						</p>
					) : null}

					{/* Números (desktop: ao lado da foto) */}
					<div
						className="hidden flex-wrap gap-2 pt-1 sm:flex"
						data-testid="professional-showcase-stats"
					>
						{isShowcaseLoading && hasId ? (
							<div className="h-9 w-56 animate-pulse rounded-full bg-zinc-100" />
						) : showNumbers && showcase ? (
							<>
								<span className="inline-flex items-baseline gap-1.5 rounded-full border border-zinc-100 bg-zinc-50 px-3 py-1.5">
									<span className="text-base font-black text-stone-900">
										{formatCount(showcase.sessions_count)}
									</span>
									<span className="text-[10px] font-black uppercase tracking-[0.14em] text-zinc-500">
										sessões realizadas
									</span>
								</span>
							</>
						) : memberSince ? (
							<span className="inline-flex items-center gap-2 rounded-full border border-zinc-100 bg-zinc-50 px-3 py-1.5 text-xs font-bold text-stone-900">
								<Users className="h-3.5 w-3.5 text-brand-orange" />
								Na Plumaa desde{' '}
								<span className="capitalize">
									{memberSince}
								</span>
							</span>
						) : null}
					</div>
				</div>
			</div>

			{/* Números no mobile (abaixo do cabeçalho) */}
			{showNumbers && showcase ? (
				<div className="flex items-baseline gap-2 rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 sm:hidden">
					<p className="text-xl font-black leading-tight text-stone-900">
						{formatCount(showcase.sessions_count)}
					</p>
					<p className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">
						sessões realizadas
					</p>
				</div>
			) : memberSince ? (
				<p className="flex items-center gap-2 text-xs font-bold text-stone-900 sm:hidden">
					<Users className="h-3.5 w-3.5 text-brand-orange" />
					Na Plumaa desde{' '}
					<span className="capitalize">{memberSince}</span>
				</p>
			) : null}

			{/* Vídeo de apresentação (autoplay) */}
			{youtubeId && isRendered ? (
				<div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-zinc-900 shadow-sm">
					<iframe
						className="absolute inset-0 h-full w-full border-0"
						src={embedSrc}
						title={`Vídeo de apresentação de ${fullName}`}
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
					/>
				</div>
			) : null}

			<div className="grid gap-6 md:grid-cols-2 md:gap-8">
				{/* Esquerda: bio, abordagem, temas */}
				<div className="space-y-6">
					<div className="space-y-3">
						<SectionLabel icon={Info}>
							{description ? 'Biografia' : 'Sobre o profissional'}
						</SectionLabel>
						<p className="text-[15px] font-semibold leading-relaxed text-zinc-700">
							{description ||
								'O profissional ainda não adicionou uma descrição.'}
						</p>
					</div>

					{mainApproachMeta || secondaryApproachMetas.length > 0 ? (
						<div className="space-y-3">
							<SectionLabel icon={Sparkles}>
								Abordagem
							</SectionLabel>
							<ul className="space-y-2">
								{mainApproachMeta ? (
									<li className="rounded-2xl border border-stone-900/10 bg-stone-50 px-4 py-3">
										<p className="text-sm font-black text-stone-900">
											{mainApproachMeta.label}
										</p>
										<p className="text-xs font-medium text-zinc-500">
											{mainApproachMeta.hint}
										</p>
									</li>
								) : null}
								{secondaryApproachMetas.map((meta) => (
									<li
										key={meta.key}
										className="rounded-2xl border border-zinc-100 px-4 py-2.5"
									>
										<p className="text-sm font-bold text-zinc-800">
											{meta.label}
											<span className="ml-2 text-[10px] font-black uppercase tracking-[0.14em] text-zinc-400">
												complementar
											</span>
										</p>
										<p className="text-xs font-medium text-zinc-500">
											{meta.hint}
										</p>
									</li>
								))}
							</ul>
						</div>
					) : null}

					{topics.length > 0 ? (
						<div className="space-y-3">
							<SectionLabel icon={Sparkles}>Temas</SectionLabel>
							<div className="flex flex-wrap gap-1.5">
								{topics.map((topic) => (
									<span
										key={topic.key}
										className="inline-flex items-center rounded-full border border-zinc-200 bg-white px-3 py-1 text-[11px] font-bold text-zinc-700"
									>
										{topic.label}
									</span>
								))}
							</div>
						</div>
					) : null}
				</div>

				{/* Direita: próximos horários */}
				<div
					className="space-y-3"
					data-testid="professional-showcase-slots"
				>
					<SectionLabel icon={CalendarClock}>
						Próximos horários
					</SectionLabel>
					{isAvailabilityLoading && hasId ? (
						<div className="grid grid-cols-2 gap-2">
							{Array.from({ length: NEXT_SLOTS_COUNT }).map(
								(_, index) => (
									<div
										key={index}
										className="h-10 animate-pulse rounded-full bg-zinc-100"
									/>
								),
							)}
						</div>
					) : nextSlots.length > 0 ? (
						<>
							<div className="grid grid-cols-2 gap-2">
								{nextSlots.map((slot) => (
									<button
										key={`${slot.available_date_id}-${slot.start_time}`}
										type="button"
										onClick={() => handleSchedule(slot)}
										disabled={!canSchedule}
										className={cn(
											'inline-flex items-center justify-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-2 text-xs font-bold text-stone-900 transition-colors',
											canSchedule
												? 'hover:border-stone-900 hover:bg-stone-900 hover:text-white'
												: 'cursor-default opacity-70',
										)}
									>
										<Clock3 className="h-3.5 w-3.5 shrink-0" />
										{formatSlotLabel(slot.start_time)}
									</button>
								))}
							</div>
							<p className="text-[11px] font-medium text-zinc-500">
								Toque num horário para ir direto à confirmação
								com ele selecionado.
							</p>
						</>
					) : (
						<p className="rounded-2xl border border-dashed border-zinc-200 px-4 py-3 text-xs font-semibold text-zinc-500">
							Sem horários nos próximos dias
						</p>
					)}
				</div>
			</div>

			{/* CTA: fixo no rodapé enquanto o painel rola (mobile e desktop) */}
			{onSchedule || previewMode ? (
				<div className="sticky bottom-0 z-20 -mx-5 -mb-5 border-t border-zinc-100 bg-white px-5 pb-4 pt-3 md:-mx-6 md:-mb-9 md:px-6 md:pb-6 lg:-mb-10">
					<button
						type="button"
						onClick={() => handleSchedule()}
						disabled={!canSchedule}
						data-testid="professional-showcase-cta"
						className={cn(
							'inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 py-3 text-sm font-black uppercase tracking-wide text-white shadow-md transition-colors',
							canSchedule
								? 'hover:bg-emerald-700'
								: 'cursor-not-allowed opacity-50',
						)}
					>
						<CalendarClock className="h-4 w-4" />
						Agendar com {first_name}
					</button>
				</div>
			) : null}
		</div>
	);
};

export { ProfessionalShowcasePanel, formatSlotLabel };
